import dayjs from "./dayjs";
import { parseAndFixUrl } from "./parseAndFixUrl";

/**
 * How recently an account must have been created to be treated as "new to the
 * platform" for onboarding purposes.
 *
 * This window is intentionally generous (5 minutes): it must comfortably cover
 * the time it takes a user to receive and enter an OTP code, or bounce through
 * an OAuth provider, after their account is created. It is still far shorter
 * than any realistic gap before a *returning* user signs in again, so returning
 * users (whose accounts are hours/days/months old) are never matched — they are
 * never funnelled into onboarding a second time.
 */
export const NEW_USER_ONBOARDING_THRESHOLD_SECONDS = 300;

/**
 * True when the account was created within the onboarding window — i.e. this is
 * a brand-new signup rather than a returning user signing back in.
 *
 * A missing/invalid `created_at` is treated as new so we fail safe toward
 * showing onboarding (matches the existing SignInWrapper behaviour).
 */
export function isNewlyCreatedAccount(
  createdAt: string | null | undefined,
  thresholdSeconds: number = NEW_USER_ONBOARDING_THRESHOLD_SECONDS
): boolean {
  const created = dayjs(createdAt).utc();
  if (!created.isValid()) return true;
  return dayjs().utc().diff(created, "seconds") < thresholdSeconds;
}

/**
 * True only for a same-app path (`/…`). Rejects absolute URLs
 * (`https://evil.com`), protocol-relative URLs (`//evil.com`), scheme payloads
 * (`javascript:…`), and empty/missing values.
 *
 * Post-auth destinations (`redirectTo` / `redirectRoute`) travel through
 * user-visible query params, so every navigation that consumes one must treat
 * it as untrusted — this is the shared guard that keeps the auth flow from
 * being used as an open redirect.
 */
export function isInternalPath(
  path: string | null | undefined
): path is string {
  return Boolean(path && path.startsWith("/") && !path.startsWith("//"));
}

/**
 * Marks a `/suggested` arrival as a guest-list signup — the cohort whose
 * stepper detour the `special_access_resolve_client_side` gate governs.
 */
export const GUEST_LIST_ONBOARDING_SOURCE = "guest-list";

/**
 * Decides where to send a user immediately after authentication completes.
 *
 * New-to-platform users are routed through the suggested-comedians onboarding
 * stepper (`/suggested`) before continuing on to their original `redirectTo`.
 * Returning users skip the stepper entirely, so they are never shown it again
 * and again.
 *
 * This is opt-in via `routeNewUserToOnboarding` so that non-fan flows (checkout,
 * creator sign-in, venue follows, etc.) keep their existing redirects untouched
 * — only the comedian "guest list" signup forms turn it on. The flag can be
 * forged in a query param (e.g. a hand-built OAuth URL), which is why it alone
 * never decides anything: a returning account fails `isNewUser`, so forcing the
 * flag on cannot resurface onboarding for them.
 *
 * The wrap carries `onboardingSource=guest-list` so `/suggested` can hold the
 * stepper behind the rollout gate for exactly this cohort.
 *
 * Kept as a pure function (no auth/router access) so the routing rules can be
 * unit-tested directly and reused across every auth-completion path
 * (email/phone OTP, OAuth, magic link).
 */
export function resolvePostAuthRedirect({
  redirectTo,
  isNewUser,
  routeNewUserToOnboarding,
}: {
  redirectTo: string;
  isNewUser: boolean;
  routeNewUserToOnboarding: boolean;
}): string {
  // Don't double-wrap a redirect that is already heading into onboarding.
  const alreadyHeadingToOnboarding = redirectTo.startsWith("/suggested");

  if (routeNewUserToOnboarding && isNewUser && !alreadyHeadingToOnboarding) {
    // Encode so a destination containing `&`/`?` survives the round trip
    // through /suggested's query string intact.
    return `/suggested?redirectTo=${encodeURIComponent(redirectTo)}&onboardingSource=${GUEST_LIST_ONBOARDING_SOURCE}`;
  }

  return redirectTo;
}

/**
 * The complete post-auth navigation URL for the OTP sign-in flow, composed as
 * one pure function so the entire decision chain — new-user detection, the
 * onboarding wrap, the welcome banner, duplicate-param cleanup, and the
 * open-redirect guard — is unit-testable without a browser. Every sign-in
 * entry point that consumes it inherits the same tested behaviour.
 *
 * Always returns a same-app path: a non-internal destination falls back to
 * `/` rather than letting the auth flow navigate off-site.
 */
export function buildPostAuthUrl({
  redirectTo,
  accountCreatedAt,
  routeNewUserToOnboarding,
}: {
  redirectTo: string;
  /** The authenticated user's `created_at` — decides new vs. returning. */
  accountCreatedAt: string | null | undefined;
  routeNewUserToOnboarding: boolean;
}): string {
  const destination = resolvePostAuthRedirect({
    redirectTo,
    isNewUser: isNewlyCreatedAccount(accountCreatedAt),
    routeNewUserToOnboarding,
  });
  const withBanner = parseAndFixUrl(`${destination}?showWelcomeBanner=1`);
  return isInternalPath(withBanner) ? withBanner : "/";
}
